import React, { useEffect, useState, useContext } from 'react';
import { useRouter } from 'next/compat/router';
import { useApolloClient } from '@apollo/client';
import { MediaContext } from 'react-media-query-hoc';
import { Message } from '@domain-group/fe-blueprint-ui/message';
import { Button } from '@domain-group/fe-blueprint-ui/button';
import SearchIcon from '@domain-group/fe-co-icon/lib/js/search';
import TypeaheadV5 from '@domain-group/fe-co-typeahead';
import TypeaheadV3 from '@domain-group/fe-co-typeahead-old';
import { GROUPS, CATEGORIES } from '@domain-group/fe-co-typeahead/lib/js/types';
import { AnimatedPlaceholderConfig } from '@domain-group/fe-co-typeahead/lib/js/util/use-animated-placeholder';

import optionType from './components/option-type';
import SuggestedSuburbsWrapper from './components/suggested-suburbs-wrapper';
import { Option } from './types';
import {
  suggestLocations,
  getRecentSearches,
  saveRecentSearches,
  getSafeStorage,
  MAX_RECENT_SEARCHES,
} from './utils';

import * as styles from './search-autocomplete.style';

export interface SearchAutocompleteProps {
  initialSelectedOption?: Option[];
  performSearchQuery?: (suburbs: Option[]) => void;
  isLocationSearchByAgent?: boolean;
  icons?: boolean;
  recentSearches?: boolean;
  appearance?: 'large' | 'medium' | 'small';
  minimumInput?: number;
  useTypeaheadV5?: boolean;
  animatedPlaceholder?: AnimatedPlaceholderConfig;
  suggestedSuburbs?: boolean;
}

const SearchAutocomplete = ({
  initialSelectedOption = [],
  performSearchQuery,
  isLocationSearchByAgent = false,
  icons = true,
  recentSearches: enableRecentSearches = false,
  appearance = 'medium',
  minimumInput = 2,
  useTypeaheadV5 = false,
  animatedPlaceholder,
  suggestedSuburbs = false,
}: SearchAutocompleteProps): JSX.Element => {
  const Typeahead = useTypeaheadV5 ? TypeaheadV5 : TypeaheadV3;
  const apolloClient = useApolloClient();
  const router = useRouter();
  const media = useContext<{ mobile?: boolean }>(MediaContext);
  const [input, setInput] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<Option[]>([]);
  const [selectedValues, setSelectedValues] = useState<Option[]>(
    initialSelectedOption,
  );
  const [error, setError] = useState<string | null>(null);
  const [noResults, setNoResults] = useState<string | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // V5 persists recent searches across browser sessions (localStorage);
  // V3 retains the legacy session-only behaviour (sessionStorage). getSafeStorage
  // resolves the storage lazily (SSR never touches `window`) and falls back to a
  // no-op when storage is blocked/unavailable.
  const recentSearchesStorageType = useTypeaheadV5 ? 'local' : 'session';

  useEffect(() => {
    if (enableRecentSearches) {
      setRecentSearches(
        getRecentSearches(getSafeStorage(recentSearchesStorageType)),
      );
    }
  }, [enableRecentSearches, recentSearchesStorageType]);

  const redirectUser = (value: string) => {
    router?.push(value, undefined, { shallow: true });
  };

  const shouldHideRecentSearches =
    !enableRecentSearches ||
    (useTypeaheadV5 && suggestedSuburbs && selectedValues.length > 0);

  const selectedValueSet = new Set(selectedValues.map(({ value }) => value));

  const recentSearchesToDisplay = shouldHideRecentSearches
    ? []
    : recentSearches
        .filter((item) => !selectedValueSet.has(item.value))
        .slice(useTypeaheadV5 ? -MAX_RECENT_SEARCHES : 0);

  const recentSearchesProp =
    useTypeaheadV5 && input.length > 0 ? [] : recentSearchesToDisplay;

  // The V5 menu opens only when it has something to show: suggested suburbs or
  // recent searches for an empty input, or enough characters to autocomplete.
  // V3 keeps its legacy always-open behaviour. (Mobile "open on any type" is
  // handled in handleInputChange, preserving master's behaviour.)
  const shouldOpenMenu = (currentInput: string = input): boolean => {
    if (!useTypeaheadV5) {
      return true;
    }
    if (currentInput.length === 0) {
      return suggestedSuburbs || recentSearchesToDisplay.length > 0;
    }
    return currentInput.length >= minimumInput;
  };

  const syncMenu = (currentInput: string = input): void => {
    setMenuOpen(shouldOpenMenu(currentInput));
  };

  const handleAutocompleteChange = (values: Option[]) => {
    const selectedItem =
      values && values.length > 0 ? values[values.length - 1] : null;

    if (selectedItem && selectedItem.redirectToURL && selectedItem.url) {
      redirectUser(selectedItem.url);
    } else {
      setSelectedValues(values);
    }
  };

  const handleRemove = ({ item }: { item: Option }) => {
    const newSelectedValues = selectedValues.filter(
      (selectedValue) => selectedValue.value !== item.value,
    );
    handleAutocompleteChange(newSelectedValues);
  };

  const handleSelect = ({ item }: { item: Option }) => {
    const newSelectedValues = [...selectedValues, item];

    handleAutocompleteChange(newSelectedValues);

    setInput('');
    if (!useTypeaheadV5) {
      // V3 closes the menu after a selection (matching the previous
      // library-driven closeMenu() behaviour).
      setMenuOpen(false);
      return;
    }
    const newSelectedSet = new Set(newSelectedValues.map(({ value }) => value));
    const hasRecentsToShow =
      enableRecentSearches &&
      !suggestedSuburbs &&
      recentSearches.some((recent) => !newSelectedSet.has(recent.value));
    setMenuOpen(suggestedSuburbs || hasRecentsToShow);
  };

  const performSearch = (
    event?:
      | React.KeyboardEvent<HTMLInputElement>
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event?.preventDefault();

    if (selectedValues.length === 0) {
      setError('Oops… we need a location or name.');
      setNoResults(null);
      return;
    }

    if (enableRecentSearches) {
      const newRecentSearches = saveRecentSearches(
        getSafeStorage(recentSearchesStorageType),
        recentSearches,
        selectedValues,
        useTypeaheadV5 ? MAX_RECENT_SEARCHES : undefined,
      );

      setRecentSearches(newRecentSearches);
    }

    if (performSearchQuery) {
      performSearchQuery(selectedValues);
    } else {
      const searchPath = isLocationSearchByAgent
        ? 'real-estate-agents'
        : 'real-estate-agencies';

      if (selectedValues.length === 1) {
        redirectUser(`/${searchPath}/${selectedValues[0].value}/`);
      } else {
        const queries = selectedValues.map((suburb) => suburb.value).join(',');
        window.location.href = `/${searchPath}/?suburbs=${encodeURIComponent(queries)}`;
      }
    }
  };

  const hasNoResults = (): JSX.Element => (
    <div css={styles.noResultsMessage}>
      Sorry, we didn’t find any matches for &quot;{input}&quot;. {noResults}
    </div>
  );

  const handleInputChange = (newInput: string) => {
    setInput(newInput);

    // master: mobile has no focus-to-open affordance, so any typing opens the
    // menu. Otherwise defer to syncMenu (HEAD's input-length/history logic).
    if (useTypeaheadV5 && media?.mobile && !menuOpen) {
      setMenuOpen(true);
    } else {
      syncMenu(newInput);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // keyCode is deprecated, but key is unavailable in some older browsers
    const isEnterKey = event.key === 'Enter' || event.keyCode === 13;
    const isEscapeKey = event.key === 'Escape';

    if (isEscapeKey) {
      setMenuOpen(false);
    } else if (input.length === 0 && isEnterKey) {
      performSearch(event);
    }
  };

  const handleBlur = () => {
    setMenuOpen(false);
  };

  const handleClick = () => {
    syncMenu();
  };

  const handleFocus = () => {
    if (!media?.mobile) {
      syncMenu();
    }
  };

  const hasExternalButton = !useTypeaheadV5;
  const activeAnimatedPlaceholder = isFullScreen
    ? undefined
    : animatedPlaceholder;

  return (
    <form
      css={styles.autocomplete(appearance)}
      data-testid="find-an-agent-autocomplete"
      onSubmit={performSearch}
    >
      <div
        css={styles.typeaheadContainer}
        data-testid="find-an-agent-autocomplete-search__search-bar-inner"
      >
        <div css={styles.typeahead(appearance, hasExternalButton)}>
          <Typeahead
            instanceId="searchautocomplete"
            enableFullScreen={media.mobile}
            // Full screen portal has a small bug where
            // the input field doesn't focus on opening
            // (ie. keyboard doesn't pop up)
            {...(!useTypeaheadV5 && { enableFullScreenPortal: true })}
            shouldSubmitImmediatelyBlockUI
            input={input}
            open={menuOpen}
            selectedItems={selectedValues}
            modes={{
              match: {
                label: 'match',
                placeholder: 'Search suburb, agent or agency',
                fetchOptions: ({
                  input: autoCompleteInput,
                }: {
                  input: string;
                }) =>
                  suggestLocations({
                    input: autoCompleteInput,
                    apolloClient,
                    setError,
                    setNoResults,
                    useTypeaheadV5,
                  }),
                onRemove: handleRemove,
                onSelect: handleSelect,
              },
            }}
            initialMode="match"
            media={useTypeaheadV5 ? media : false}
            appearance={appearance}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onClick={handleClick}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onBackClick={handleBlur}
            recentSearches={recentSearchesProp}
            onOpen={({ isFullScreen: fs }: { isFullScreen: boolean }) =>
              setIsFullScreen(fs)
            }
            onClose={({ isFullScreen: fs }: { isFullScreen: boolean }) => {
              setIsFullScreen(fs);
              handleBlur();
            }}
            optionType={optionType(icons, useTypeaheadV5)}
            showKeyword={false}
            showNoOptionsMessage={false}
            customMessage={
              !!noResults && input.length >= minimumInput
                ? hasNoResults
                : undefined
            }
            minimumInput={minimumInput}
            // eslint-disable-next-line react/no-unstable-nested-components
            rhs={({ isFullScreen: isTypeaheadFullScreen }: any) => {
              if (isTypeaheadFullScreen) {
                if (useTypeaheadV5) {
                  return (
                    <div css={styles.searchButtonInlineWrapper}>
                      <Button
                        type="button"
                        scope="embedded"
                        icon={<SearchIcon size="custom" />}
                        aria-label="Search"
                        onClick={performSearch}
                      />
                    </div>
                  );
                }
                return (
                  <div css={styles.searchButtonFullScreen}>
                    <button
                      type="button"
                      onClick={performSearch}
                      css={styles.searchButton(
                        appearance,
                        isTypeaheadFullScreen,
                      )}
                    >
                      <span css={styles.srOnly}>Search</span>
                      <SearchIcon size="custom" />
                    </button>
                  </div>
                );
              }
              if (useTypeaheadV5) {
                return (
                  <div css={styles.searchButtonInlineWrapper}>
                    <Button
                      type="submit"
                      scope="embedded"
                      icon={<SearchIcon size="custom" />}
                      aria-label="Search"
                    />
                  </div>
                );
              }
              return null;
            }}
            {...(useTypeaheadV5 && {
              useDesignTokens: true,
              condenseOnBlur: true,
              showOptionCategory: false,
              groups: [GROUPS.LOCATIONS, GROUPS.AGENTS, GROUPS.AGENCIES],
              standaloneCategories: [CATEGORIES.AGENT, CATEGORIES.AGENCY],
              ...(suggestedSuburbs && {
                upperContent: SuggestedSuburbsWrapper,
              }),
            })}
            {...(activeAnimatedPlaceholder && {
              animatedPlaceholder: activeAnimatedPlaceholder,
            })}
          />
        </div>
        {!useTypeaheadV5 && (
          <button type="submit" css={styles.searchButton(appearance)}>
            <span css={styles.srOnly}>Search</span>
            <SearchIcon size="custom" />
          </button>
        )}
      </div>

      {error && (
        <div
          css={styles.errorMessage}
          data-testid="find-an-agent-autocomplete__error-message"
        >
          <Message type="error" headline={error} />
        </div>
      )}
    </form>
  );
};

export default SearchAutocomplete;
